import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import GuestBanner from '../components/GuestBanner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Image,
  Video,
  Mic,
  FileText,
  FolderPlus,
  Plus,
  Heart,
  Eye,
  Trash2,
  Lock,
  Globe,
  Loader2,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const mediaTypeIcons = {
  photo: Image,
  video: Video,
  audio: Mic,
  note: FileText
};

const MediaAlbums = () => {
  const { user } = useAuth();
  const [albums, setAlbums] = useState([]);
  const [myAlbums, setMyAlbums] = useState([]);
  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [albumMedia, setAlbumMedia] = useState([]);
  const [addAlbumOpen, setAddAlbumOpen] = useState(false);
  const [addMediaOpen, setAddMediaOpen] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);
  const [saving, setSaving] = useState(false);

  const [albumForm, setAlbumForm] = useState({
    name: '',
    description: '',
    pet_id: '',
    is_public: true,
    cover_image_url: ''
  });

  const [mediaForm, setMediaForm] = useState({
    media_type: 'photo',
    title: '',
    description: '',
    url: '',
    content: '',
    tags: ''
  });

  useEffect(() => {
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      const albumsRes = await axios.get(`${API}/api/albums`);
      setAlbums(albumsRes.data);

      if (user) {
        const [myRes, petsRes] = await Promise.all([
          axios.get(`${API}/api/albums/my-albums`),
          axios.get(`${API}/api/pets/my-pets`)
        ]);
        setMyAlbums(myRes.data);
        setPets(petsRes.data);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAlbumMedia = async (albumId) => {
    try {
      const res = await axios.get(`${API}/api/albums/${albumId}/media`);
      setAlbumMedia(res.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleSelectAlbum = async (album) => {
    setSelectedAlbum(album);
    await fetchAlbumMedia(album.id);
  };

  const handleCreateAlbum = async () => {
    if (!albumForm.name) {
      toast.error('Album name is required');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/api/albums`, {
        ...albumForm,
        pet_id: albumForm.pet_id || null
      });
      toast.success('Album created!');
      setAddAlbumOpen(false);
      setAlbumForm({ name: '', description: '', pet_id: '', is_public: true, cover_image_url: '' });
      fetchData();
      handleSelectAlbum(res.data);
    } catch (error) {
      toast.error('Failed to create album');
    } finally {
      setSaving(false);
    }
  };

  const handleAddMedia = async () => {
    if (!mediaForm.url && mediaForm.media_type !== 'note') {
      toast.error('URL is required for this media type');
      return;
    }
    if (!mediaForm.content && mediaForm.media_type === 'note') {
      toast.error('Note content is required');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/api/albums/${selectedAlbum.id}/media`, {
        ...mediaForm,
        album_id: selectedAlbum.id,
        tags: mediaForm.tags.split(',').map(t => t.trim()).filter(Boolean)
      });
      toast.success('Media added!');
      setAddMediaOpen(false);
      setMediaForm({ media_type: 'photo', title: '', description: '', url: '', content: '', tags: '' });
      fetchAlbumMedia(selectedAlbum.id);
      // Refresh album to update counts
      const albumRes = await axios.get(`${API}/api/albums/${selectedAlbum.id}`);
      setSelectedAlbum(albumRes.data);
    } catch (error) {
      toast.error('Failed to add media');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMedia = async (mediaId) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await axios.delete(`${API}/api/albums/${selectedAlbum.id}/media/${mediaId}`);
      toast.success('Deleted');
      fetchAlbumMedia(selectedAlbum.id);
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleLikeMedia = async (mediaId) => {
    if (!user) {
      toast.error('Please log in');
      return;
    }
    try {
      await axios.post(`${API}/api/albums/${selectedAlbum.id}/media/${mediaId}/like`);
      fetchAlbumMedia(selectedAlbum.id);
    } catch (error) {
      toast.error('Failed');
    }
  };

  const MediaIcon = ({ type }) => {
    const Icon = mediaTypeIcons[type] || Image;
    return <Icon className="w-5 h-5" />;
  };

  const openViewer = (index) => {
    const photoMedia = albumMedia.filter(m => m.media_type === 'photo' || m.media_type === 'video');
    if (photoMedia.length > 0) {
      setViewerIndex(index);
      setViewerOpen(true);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const viewableMedia = albumMedia.filter(m => m.media_type === 'photo' || m.media_type === 'video');

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <GuestBanner message="Sign up to create albums and share memories!" />

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-heading font-medium flex items-center gap-3">
              <Image className="w-8 h-8 text-primary" />
              Media Albums
            </h1>
            <p className="text-muted-foreground">
              Organize photos, videos, audio, and notes for your pets
            </p>
          </div>
          {user && (
            <Dialog open={addAlbumOpen} onOpenChange={setAddAlbumOpen}>
              <DialogTrigger asChild>
                <Button data-testid="create-album-btn">
                  <FolderPlus className="w-4 h-4 mr-2" />
                  Create Album
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create Album</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <Input placeholder="Album Name *" value={albumForm.name} onChange={(e) => setAlbumForm({...albumForm, name: e.target.value})} />
                  <Textarea placeholder="Description" value={albumForm.description} onChange={(e) => setAlbumForm({...albumForm, description: e.target.value})} rows={2} />
                  {pets.length > 0 && (
                    <Select value={albumForm.pet_id || 'none'} onValueChange={(val) => setAlbumForm({...albumForm, pet_id: val === 'none' ? '' : val})}>
                      <SelectTrigger><SelectValue placeholder="Link to pet (optional)" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No pet</SelectItem>
                        {pets.map(pet => <SelectItem key={pet.id} value={pet.id}>{pet.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                  <Input placeholder="Cover Image URL" value={albumForm.cover_image_url} onChange={(e) => setAlbumForm({...albumForm, cover_image_url: e.target.value})} />
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={albumForm.is_public} onChange={(e) => setAlbumForm({...albumForm, is_public: e.target.checked})} />
                    <span className="text-sm">Public album</span>
                  </label>
                  <Button onClick={handleCreateAlbum} className="w-full" disabled={saving}>
                    {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    Create Album
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>

        <div className="grid lg:grid-cols-4 gap-6">
          {/* Albums Sidebar */}
          <div className="space-y-4">
            <h3 className="font-heading font-medium">Albums</h3>
            {(user ? myAlbums : albums).length === 0 ? (
              <Card className="text-center py-8">
                <FolderPlus className="w-8 h-8 mx-auto text-muted-foreground/30 mb-2" />
                <p className="text-sm text-muted-foreground">No albums yet</p>
              </Card>
            ) : (
              (user ? myAlbums : albums).map(album => (
                <Card 
                  key={album.id}
                  className={`cursor-pointer transition-all ${selectedAlbum?.id === album.id ? 'ring-2 ring-primary' : 'hover:shadow-md'}`}
                  onClick={() => handleSelectAlbum(album)}
                  data-testid={`album-${album.id}`}
                >
                  <CardContent className="p-3">
                    <div className="flex gap-3">
                      {album.cover_image_url ? (
                        <img src={album.cover_image_url} alt={album.name} className="w-16 h-16 rounded-lg object-cover" />
                      ) : (
                        <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center">
                          <Image className="w-6 h-6 text-muted-foreground" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1">
                          <h4 className="font-medium text-sm truncate">{album.name}</h4>
                          {album.is_public ? <Globe className="w-3 h-3 text-muted-foreground" /> : <Lock className="w-3 h-3 text-muted-foreground" />}
                        </div>
                        {album.pet_name && <p className="text-xs text-primary">{album.pet_name}</p>}
                        <div className="flex gap-2 mt-1 text-xs text-muted-foreground">
                          <span>{album.photo_count} photos</span>
                          <span>{album.video_count} videos</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          {/* Album Content */}
          <div className="lg:col-span-3">
            {selectedAlbum ? (
              <>
                {/* Album Header */}
                <Card className="mb-6">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-xl font-heading font-medium">{selectedAlbum.name}</h2>
                        {selectedAlbum.description && <p className="text-sm text-muted-foreground">{selectedAlbum.description}</p>}
                        <div className="flex gap-4 mt-2 text-sm text-muted-foreground">
                          <span>{selectedAlbum.photo_count} photos</span>
                          <span>{selectedAlbum.video_count} videos</span>
                          <span>{selectedAlbum.audio_count} audio</span>
                          <span>{selectedAlbum.note_count} notes</span>
                        </div>
                      </div>
                      {user && selectedAlbum.owner_id === user.id && (
                        <Dialog open={addMediaOpen} onOpenChange={setAddMediaOpen}>
                          <DialogTrigger asChild>
                            <Button data-testid="add-media-btn">
                              <Plus className="w-4 h-4 mr-2" />
                              Add Media
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Add Media</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 mt-4">
                              <Select value={mediaForm.media_type} onValueChange={(val) => setMediaForm({...mediaForm, media_type: val})}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="photo">Photo</SelectItem>
                                  <SelectItem value="video">Video</SelectItem>
                                  <SelectItem value="audio">Audio</SelectItem>
                                  <SelectItem value="note">Note</SelectItem>
                                </SelectContent>
                              </Select>
                              <Input placeholder="Title" value={mediaForm.title} onChange={(e) => setMediaForm({...mediaForm, title: e.target.value})} />
                              {mediaForm.media_type !== 'note' ? (
                                <Input placeholder="Media URL *" value={mediaForm.url} onChange={(e) => setMediaForm({...mediaForm, url: e.target.value})} />
                              ) : (
                                <Textarea placeholder="Note content *" value={mediaForm.content} onChange={(e) => setMediaForm({...mediaForm, content: e.target.value})} rows={5} />
                              )}
                              <Textarea placeholder="Description" value={mediaForm.description} onChange={(e) => setMediaForm({...mediaForm, description: e.target.value})} rows={2} />
                              <Input placeholder="Tags (comma-separated)" value={mediaForm.tags} onChange={(e) => setMediaForm({...mediaForm, tags: e.target.value})} />
                              <Button onClick={handleAddMedia} className="w-full" disabled={saving}>
                                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                                Add Media
                              </Button>
                            </div>
                          </DialogContent>
                        </Dialog>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Media Grid */}
                {albumMedia.length === 0 ? (
                  <Card className="text-center py-16">
                    <Image className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                    <h3 className="font-heading text-xl mb-2">No media yet</h3>
                    <p className="text-muted-foreground">Add photos, videos, or notes to this album</p>
                  </Card>
                ) : (
                  <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {albumMedia.map((media, idx) => (
                      <Card key={media.id} className="overflow-hidden group" data-testid={`media-${media.id}`}>
                        {media.media_type === 'photo' && (
                          <div className="aspect-square cursor-pointer" onClick={() => openViewer(viewableMedia.findIndex(m => m.id === media.id))}>
                            <img src={media.url} alt={media.title || 'Photo'} className="w-full h-full object-cover" />
                          </div>
                        )}
                        {media.media_type === 'video' && (
                          <div className="aspect-square bg-muted flex items-center justify-center cursor-pointer" onClick={() => openViewer(viewableMedia.findIndex(m => m.id === media.id))}>
                            <Video className="w-12 h-12 text-muted-foreground" />
                          </div>
                        )}
                        {media.media_type === 'audio' && (
                          <div className="aspect-square bg-muted flex items-center justify-center">
                            <Mic className="w-12 h-12 text-muted-foreground" />
                          </div>
                        )}
                        {media.media_type === 'note' && (
                          <div className="aspect-square bg-amber-50 p-4 overflow-hidden">
                            <p className="text-sm line-clamp-6">{media.content}</p>
                          </div>
                        )}
                        <CardContent className="p-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <MediaIcon type={media.media_type} />
                              <span className="text-sm font-medium truncate">{media.title || media.media_type}</span>
                            </div>
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleLikeMedia(media.id)}>
                                <Heart className={`w-4 h-4 ${media.like_count > 0 ? 'fill-red-500 text-red-500' : ''}`} />
                              </Button>
                              {user && selectedAlbum.owner_id === user.id && (
                                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDeleteMedia(media.id)}>
                                  <Trash2 className="w-4 h-4 text-destructive" />
                                </Button>
                              )}
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground">{media.like_count} likes</p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <Card className="text-center py-16">
                <FolderPlus className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                <h3 className="font-heading text-xl mb-2">Select an album</h3>
                <p className="text-muted-foreground">Choose an album to view its contents</p>
              </Card>
            )}
          </div>
        </div>

        {/* Image Viewer */}
        <Dialog open={viewerOpen} onOpenChange={setViewerOpen}>
          <DialogContent className="max-w-4xl p-0 overflow-hidden">
            {viewableMedia.length > 0 && (
              <div className="relative">
                {viewableMedia[viewerIndex]?.media_type === 'photo' ? (
                  <img src={viewableMedia[viewerIndex]?.url} alt="" className="w-full max-h-[80vh] object-contain" />
                ) : (
                  <video src={viewableMedia[viewerIndex]?.url} controls className="w-full max-h-[80vh]" />
                )}
                {viewableMedia.length > 1 && (
                  <>
                    <Button variant="ghost" size="icon" className="absolute left-2 top-1/2 -translate-y-1/2 bg-background/80" onClick={() => setViewerIndex(i => i === 0 ? viewableMedia.length - 1 : i - 1)}>
                      <ChevronLeft className="w-6 h-6" />
                    </Button>
                    <Button variant="ghost" size="icon" className="absolute right-2 top-1/2 -translate-y-1/2 bg-background/80" onClick={() => setViewerIndex(i => i === viewableMedia.length - 1 ? 0 : i + 1)}>
                      <ChevronRight className="w-6 h-6" />
                    </Button>
                  </>
                )}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-background/80 px-3 py-1 rounded-full text-sm">
                  {viewerIndex + 1} / {viewableMedia.length}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default MediaAlbums;
